import { FC, useState } from "react";
import { useAtomValue } from "jotai";

import {
  LocationAccessEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { JunkLoadStatusEnum } from "@/domain/models/job";
import { i18n } from "@/i18n";

import { OptionType } from "../blocks";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { UpdateServicePropsType } from "../domain/service";
import { JobServiceTaskBaseType } from "../domain/task";
import {
  isDriverReviewingJunkLoadAtom,
  isDriverReviewingTaskAtom,
} from "../stores/atoms";
import { DriverScreenWithOptions } from "./DriverScreenWithOptions";

const LOCATION_ACCESS_OPTIONS: OptionType<LocationAccessEnum>[] = [
  {
    label: i18n.t("driver_location_access_screen.easy"),
    value: LocationAccessEnum.EASY,
  },
  {
    label: i18n.t("driver_location_access_screen.difficult"),
    value: LocationAccessEnum.DIFFICULT,
  },
];

const DriverLocationAccessStep: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const [selectedLocationAccess, setSelectedLocationAccess] =
    useState<LocationAccessEnum | null>(null);

  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);
  const isDriverReviewingJunkLoad = useAtomValue(isDriverReviewingJunkLoadAtom);

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const getNextDriverFlowStatus = () => {
    switch (true) {
      case isDriverReviewingTask:
        return TaskDriverStatusEnum.TASK_REVIEW;
      case isDriverReviewingJunkLoad:
        return TaskDriverStatusEnum.JUNK_LOAD_REVIEW;
      default:
        return TaskDriverStatusEnum.TAKING_PHOTO_AFTER_CLEANUP;
    }
  };

  const onNextClick = () => {
    if (selectedLocationAccess) {
      const driverFlowStatus = getNextDriverFlowStatus();

      const updateServiceParams: UpdateServicePropsType = {
        jobId,
        serviceId,
        driverFlowStatus,
      };

      // Check if there's a list of junk loads
      const junkLoadInProgressIndex =
        serviceData?.junkLoads?.findIndex(
          junkLoad => junkLoad.status === JunkLoadStatusEnum.IN_PROGRESS,
        ) ?? -1;

      // If there's one in progress, means driver is managing data for that specific load
      if (junkLoadInProgressIndex !== -1 && serviceData?.junkLoads) {
        const updatedJunkLoad = serviceData.junkLoads[junkLoadInProgressIndex];

        updatedJunkLoad.locationAccess = selectedLocationAccess;

        // Change back to completed status if driver is reviewing the task
        // this one is when driver is reviewing the task and pressed to edit the value
        if (isDriverReviewingTask) {
          updatedJunkLoad.status = JunkLoadStatusEnum.COMPLETED;
        }

        serviceData.junkLoads.splice(
          junkLoadInProgressIndex,
          1,
          updatedJunkLoad,
        );

        updateServiceParams.junkLoads = [...serviceData.junkLoads];
      } else {
        updateServiceParams.locationAccess = selectedLocationAccess;
      }

      updateService(updateServiceParams);

      updateTask({
        jobId,
        serviceId,
        taskId,
        driverFlowStatus,
      });
    }
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.TAKING_PHOTO_AFTER_CLEANUP;
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus === TaskDriverStatusEnum.FILLING_LOAD_SIZE;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_LOAD_SIZE,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_LOAD_SIZE,
    });
  };

  return (
    <DriverScreenWithOptions
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_location_access_screen.title"
      optionsSelectorProps={{
        onSelect: setSelectedLocationAccess,
        value: selectedLocationAccess,
        options: LOCATION_ACCESS_OPTIONS,
      }}
      bottomButtonProps={{
        onNextButtonClick: onNextClick,
        isNextButtonLoading,
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        isNextButtonDisabled: !selectedLocationAccess,
        previousButtonTitle: "driver_location_access_screen.back_button",
        onPreviousButtonClick: isDriverReviewingTask
          ? undefined
          : () => goToPreviousStep(),
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverLocationAccessStep };
